<template>
  <div class="unsolicited-npc-effect">
    <character-select
      :selected="state.selectedNpcKey"
      :characters="getNpcCharacters(emitter.caseConf)"
      :disabled="readonly"
      :errors="missingSourceCharacterErrors"
      placeholder="Select VIP"
      @input="selectNpc"
    />
    <unsolicited-items
      :emitter="emitter"
      :items="selectedNpcItems"
      :selected="state.selectedItemId"
      :control="control"
      :readonly="readonly"
      @update="selectItem"
    />
    <div class="el-input-group el-input-group--append el-input-group--prepend">
      <div class="el-input-group__prepend">To</div>
      <character-select
        :selected="state.targetCharacterKey"
        :characters="getTargetCharacters(emitter.caseConf)"
        :disabled="readonly"
        :errors="missingTargetCharacterErrors"
        placeholder="Select Target Character"
        @input="selectTargetCharacter"
      />
    </div>
    <wait-for-previous-checkbox
      :value="waitForPrevious"
      :disabled="readonly || !hasUnsolicitedPredecessor"
      @change="selectWaitForPrevious"
    />
  </div>
</template>

<script>
import get from 'lodash/get';

import { getNpcCharacters, getTargetCharacters, getCharacter } from '~lib/common/utils/characters';
import nodeMixins from './mixins/NodeMixins';

import CharacterSelect from './CharacterSelect.vue';
import UnsolicitedItems from './UnsolicitedItems.vue';
import WaitForPreviousCheckbox from './common/WaitForPreviousCheckbox.vue';
import waitForPreviousMixins from './mixins/WaitForPreviousMixins';

export default {
  name: 'unsolicited-npc-effect',
  components: {
    WaitForPreviousCheckbox,
    CharacterSelect,
    UnsolicitedItems,
  },
  mixins: [ nodeMixins, waitForPreviousMixins ],
  props: ['readonly', 'emitter', 'ikey', 'getData', 'putData', 'control', 'type'],
  data() {
    return {
      state: {
        selectedNpcKey: null,
        selectedItemId: null,
        targetCharacterKey: null,
      },
    };
  },
  computed: {
    // waitForPreviousMixins contract.
    waitForPreviousSpeaker() {
      return this.state.selectedNpcKey || null;
    },
    missingSourceCharacterErrors() {
      return this.getErrorsById('missingSourceCharacter');
    },
    missingTargetCharacterErrors() {
      return this.getErrorsById('missingTargetCharacter');
    },
    selectedNpc() {
      const npcs = this.emitter.caseConf.npcs || [];
      return npcs.find(npc => npc.key === this.state.selectedNpcKey);
    },
    selectedNpcItems() {
      if (!this.selectedNpc) {
        return [];
      }
      const dialogue = this.selectedNpc.dialogue || {};
      return dialogue.unsolicited || [];
    },
    targetCharacterLabel() {
      const targetCharacter = getCharacter(this.emitter.caseConf, this.state.targetCharacterKey) || {};
      return targetCharacter.label;
    },
  },
  created() {
    const data = this.getData(this.ikey);
    this.state.selectedNpcKey = get(data, 'npcKey');
    this.state.selectedItemId = get(data, 'id');
    this.state.targetCharacterKey = get(data, 'targetCharacterKey');
    this.loadWaitForPrevious(data);
  },
  mounted() {
    this.applyLegacyTargetCharacter();
    this.emitter.trigger('process', this.control);
  },
  methods: {
    applyLegacyTargetCharacter() {
      // Legacy effects were saved before target characters existed and have no
      // targetCharacterKey at all; default those to user (PROVIDER) so loading an
      // existing case doesn't cause a missingTargetCharacter error. An explicit
      // null means a new node still awaiting a selection, so leave it alone.
      if (this.state.selectedItemId && this.state.targetCharacterKey === undefined) {
        this.state.targetCharacterKey = 'user';
        this.update();
      }
    },
    selectNpc(npcKey) {
      this.state.selectedNpcKey = npcKey;
      this.state.selectedItemId = null;
      // First time selecting an npc, we don't want to emit null id and cause validation errors
      if (!this.getData(this.ikey)) {
        return;
      }
      this.update();
    },
    selectItem(itemId) {
      this.state.selectedItemId = itemId;
      // Mark a new node null so it requires an explicit
      // choice instead of being silently defaulted to User.
      if (this.state.targetCharacterKey === undefined) {
        this.state.targetCharacterKey = null;
      }
      this.update();
    },
    selectTargetCharacter(key) {
      this.state.targetCharacterKey = key;
      this.update();
    },
    update() {
      this.putData(this.ikey, {
        type: 'NPC_UNSOLICITED_DIALOGUE',
        id: this.state.selectedItemId,
        npcKey: this.state.selectedNpcKey,
        targetCharacterKey: this.state.targetCharacterKey,
        // waitForPreviousMixins reconciles this value.
        waitForPrevious: this.waitForPrevious,
      });
      this.emitter.trigger('process', this.control);
    },
    getNpcCharacters,
    getTargetCharacters,
  },
}
</script>

<style scoped>
</style>